import { SortDirectionType, SortingOptionsType } from '@components/design/migrated/Table';
import { Dispatch, SetStateAction } from 'react';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { Country as Countries, FactRevenue, Filters, Option } from '../utils/pageConfig/Types';
import { RelativePeriod } from '@components/comparison-period-selector/Types';
import { ProductDataType, ProductType } from '@pages/product-analyzer/Product';
import { DEFAULT_VOLUME } from '@utils/factVolumeUtils/FactVolumeUtils';
import { BaseTimePeriod } from '@services/FiltersTypes';
import { CollectionLiveData } from '@components/collections/Model';

export enum SaleType {
  VALUE = 'VALUE',
  VOLUME = 'VOLUME',
}

export enum RetailerType {
  MARKET_REVENUE = 'market',
  MY_REVENUE = 'retailer',
}

export enum PeriodType {
  MAT = 'MAT',
  YTD = 'YTD',
}

export interface BubbleChartParam extends CollectionLiveData {
  clientId: number;
  productType: ProductType;
  productGroup: Option[];
  country: Countries[];
  period: string;
  comparisonPeriod: RelativePeriod | BaseTimePeriod;
  periodType: Periodicity;
  saleType: SaleType;
  segmentDataType: string;
  segmentName: string;
  appliedFilters: Filters[];
  isSummary: boolean;
}

export interface TableParam {
  clientId: number;
  productType: ProductType;
  countryIds: string[];
  productGroup: Option[] | undefined;
  period: string;
  comparisonPeriod: RelativePeriod | BaseTimePeriod;
  periodType: Periodicity;
  pageRequest: PageRequest;
  sortOption: SortingOptionsType;
  growthType: GrowthTypes;
  appliedFilters: Filters[];
  segment: string;
  dataType: string;
  oRGroups: OpportunityReviewBoardGroupDetails[];
}

export interface BubbleChartOptionsParam {
  data: BubbleChartResp;
  retailerType: RetailerType;
  selectedPeriod: string;
  selectedGrowthType: GrowthTypes;
  factRevenue: FactRevenue;
  saleType: SaleType;
  isDataChanged: boolean;
  setIsDataChanged: Dispatch<SetStateAction<boolean>>;
  axesRange?: AxesRange;
  axisRangeMax?: number;
  isExportModalOpen: boolean;
  selectedBrandBubble: string;
  setSelectedBrandBubble: Dispatch<SetStateAction<string>>;
  volume: string;
}

export class BubbleChartResp {
  salesRecordsFound = false;
  country: Country = new Country();
  productGroup: string = '';
  segmentType: string = '';
  content: BubbleChartRespContent[] = [];
}

export class BubbleChartRespContent {
  saleType: string = '';
  market: BaseChartResp[] = [];
  retailer: BaseChartResp[] = [];
}

export class BaseChartResp {
  fairShareNumber: number = 0;
  period: string = '';
  values: BaseChartValue[] = [];
}

export class BaseChartValue {
  title: string = '';
  xcoordinate: number = 0;
  ycoordinate: number = 0;
  marketValue: number = 0;
  yourValue: number = 0;
  yourShare: number = 0;
  yourShareGrowth: number = 0;
  yourGrowthPercentage: number = 0;
  yourGrowthAbsolute: number = 0;
  growthOpportunity: number = 0;
  shareOpportunity: number = 0;
}

export class ExportBubbleChartResp {
  salesRecordsFound = false;
  content: ExportBubbleChartContent[] = [];
}

export class ExportBubbleChartContent {
  title: string = '';
  values: ExportBubbleChartBase[] = [];
}

export class ExportBubbleChartBase {
  fairShareNumber: number = 0;
  period: string = '';
}

export class Country {
  countryCode: string = '';
  countryName: string = '';
  currency: Currency = new Currency();
  stCountryId: number = 0;
}

export class Currency {
  currencyCode: string = '';
  currencyName: string = '';
  stCurrencyId: number = 0;
}

interface Kpi {
  value: number;
  percentage: number;
  actual: number;
}

interface KpiPercentage {
  growthPercentage: number;
  sharePercentage: number;
}

export interface KpiNoGrowth {
  value: number;
}

export interface SwotCategory {
  value: string;
  swotStrength: string;
}

export const CategoryGroup = 'categoryGroup';

export type ProductGroupKpi =
  | 'marketRevenue'
  | 'marketVolume'
  | 'myRevenue'
  | 'myVolume'
  | 'growthPerformanceRevenue'
  | 'growthPerformanceVolume'
  | 'fairSharePerformanceRevenue'
  | 'fairSharePerformanceVolume'
  | 'growthOppRevenue'
  | 'growthOppVolume'
  | 'fairShareOppRevenue'
  | 'fairShareOppVolume'
  | 'swotCategoryRevenue'
  | 'swotCategoryVolume';

export enum ColumnHeaderForOR {
  checkbox = 'checkbox',
  categoryGroup = 'categoryGroup',
  marketRevenue = 'marketRevenue',
  marketVolume = 'marketVolume',
  myRevenue = 'myRevenue',
  myVolume = 'myVolume',
  myRevenueShare = 'myShareRevenue',
  myVolumeShare = 'myShareVolume',
  overUnderGrowthRevenue = 'growthPerformanceRevenue',
  overUnderGrowthVolume = 'growthPerformanceVolume',
  overUnderFairShareRevenue = 'fairSharePerformanceRevenue',
  overUnderFairShareVolume = 'fairSharePerformanceVolume',
  growthOpportunityRevenue = 'growthOppRevenue',
  growthOpportunityVolume = 'growthOppVolume',
  fairShareOpportunityRevenue = 'fairShareOppRevenue',
  fairShareOpportunityVolume = 'fairShareOppVolume',
  swotCategoryRevenue = 'swotCategoryRevenue',
  swotCategoryVolume = 'swotCategoryVolume',
}

export const ColumnHeaderDisplayNameForOR = (volume: string = DEFAULT_VOLUME) => ({
  [ColumnHeaderForOR.categoryGroup]: 'Market',
  [ColumnHeaderForOR.checkbox]: 'CheckBox',
  [ColumnHeaderForOR.marketRevenue]: 'Market Revenue',
  [ColumnHeaderForOR.marketVolume]: `Market ${volume}`,
  [ColumnHeaderForOR.myRevenue]: 'Retailer Revenue',
  [ColumnHeaderForOR.myVolume]: `Retailer ${volume}`,
  [ColumnHeaderForOR.myRevenueShare]: 'Retailer Revenue Share',
  [ColumnHeaderForOR.myVolumeShare]: `Retailer ${volume} Share`,
  [ColumnHeaderForOR.overUnderGrowthRevenue]: 'Over/Under Perform Market Growth',
  [ColumnHeaderForOR.overUnderGrowthVolume]: 'Over/Under Perform Market Growth',
  [ColumnHeaderForOR.overUnderFairShareRevenue]: 'Over/Under Perform Fair Share',
  [ColumnHeaderForOR.overUnderFairShareVolume]: 'Over/Under Perform Fair Share',
  [ColumnHeaderForOR.growthOpportunityRevenue]: 'Revenue Growth Opp',
  [ColumnHeaderForOR.growthOpportunityVolume]: `${volume} Growth Opp`,
  [ColumnHeaderForOR.fairShareOpportunityRevenue]: 'Revenue Fair Share Opp',
  [ColumnHeaderForOR.fairShareOpportunityVolume]: `${volume} Fair Share Opp`,
  [ColumnHeaderForOR.swotCategoryRevenue]: 'Overall Assessment',
  [ColumnHeaderForOR.swotCategoryVolume]: 'Overall Assessment',
});

export enum SortKeys {
  Absolute = '',
  Change = 'Change',
}

export enum SortLabels {
  Absolute = 'Absolute',
  Change = 'Change',
  Ascending = 'Ascending',
  Descending = 'Descending',
}

export enum ExportChartType {
  Table = 'exportTable',
  Bubble = 'exportBubbleChart',
}

export interface OpportunityReviewTableContent {
  segment: string;
  marketRevenue: Kpi;
  marketVolume: Kpi;
  myRevenue: Kpi;
  myVolume: Kpi;
  myShareRevenue: KpiPercentage;
  myShareVolume: KpiPercentage;
  growthPerformanceRevenue: string;
  growthPerformanceVolume: string;
  fairSharePerformanceRevenue: string;
  fairSharePerformanceVolume: string;
  growthOppRevenue: KpiNoGrowth;
  growthOppVolume: KpiNoGrowth;
  fairShareOppRevenue: KpiNoGrowth;
  fairShareOppVolume: KpiNoGrowth;
  swotCategoryRevenue: SwotCategory;
  swotCategoryVolume: SwotCategory;
  grouping: string;
}

class PageableContent {
  content: OpportunityReviewTableContent[] = [];
  pageable: Pageable = new Pageable();
}

class Pageable {
  pageNumber: number = 0;
  pageSize: number = 0;
  totalElements: number = 0;
  totalPage: number = 0;
}

export class OpportunityReviewTableResponse {
  productGroup: string = '';
  country: Country = new Country();
  content?: PageableContent = new PageableContent();
  groupContent?: OpportunityReviewBoardGroupingPgKpi[];
  salesRecordsFound: boolean = true;
}

export interface PageRequest {
  page: number;
  size: number;
}

export interface OpportunityReviewBoardParam {
  clientId: number;
  productType: ProductType;
  productDataType: ProductDataType;
  countryIds: string[];
  productGroupForOpportunityReview: Option[] | undefined;
  period: string;
  comparisonPeriod: RelativePeriod | BaseTimePeriod;
  periodType: Periodicity;
  appliedFilters: Filters[];
  segment: string;
  dataType: string;
  oRGroups: OpportunityReviewBoardGroupDetails[];
}

export interface OpportunityReviewBoardReq {
  clientId: number;
  productType: ProductType;
  productDataType: ProductDataType;
  productGroup: string[];
  country: string[];
  period: string;
  comparisonPeriod: string;
  periodType: string;
  priceBand: string[];
  shareType: string;
  showPerformance: string;
  dataType: string;
  split: string[];
  channel: string[];
  region: string[];
  city: string[];
  brand: string[];
  segmentFeature: string[];
  feature: string;
  featureValue: string[];
  oRGroups: OpportunityReviewBoardGroupDetails[];
  targetCurrency?: string;
}

export const TableId = 'opportunity-review-table';

export const DefaultSortingOptionsForOpportunityReview: SortingOptionsType = {
  headerKey: ColumnHeaderForOR.myRevenue,
  sortKey: SortKeys.Absolute,
  sortDir: SortDirectionType.desc ?? 'desc',
  label: SortLabels.Absolute,
};

export interface OpportunityReviewPageResponses {
  bubbleChartResponse: BubbleChartResp;
  swotTableResponse: OpportunityReviewTableContent[];
}

export interface WeeklyMonthlyFeatures {
  weekly: Filters[];
  monthly: Filters[];
}

export const isMarketRetailerType = (retailerType: RetailerType): boolean => {
  return retailerType === RetailerType.MARKET_REVENUE;
};

export type AxisRange = Pick<Highcharts.Axis, 'max' | 'min'>;

export interface AxesRange {
  xAxisRange: AxisRange;
  yAxisRange: AxisRange;
}

export interface AxisPaddingOptions {
  axisMaxPaddingCalc: (max: number) => number;
  axisMinPaddingCalc: (min: number) => number;
}

export interface ExportChartTypeAndParams {
  type: ExportChartType;
  param: TableParam | BubbleChartParam;
}

//Group
export interface OpportunityReviewBoardGroupDetails {
  groupName: string;
  groupElement: string[];
  showPerformanceWithin?: string;
  sortOrder?: number;
  isPreferenceOwner?: boolean;
}

export interface GroupState {
  groupName?: string;
  enableIsEditGroupMode?: () => void;
  isGroupingHeader?: boolean;
  isGroupEmpty?: boolean;
  isGroupingProductGroup?: boolean;
  setIsGroupingRowExpanded?: (value: boolean) => void;
  onDeleteGroupPgs?: (groupElement: string) => void;
  isPreferenceOwner?: boolean;
}

export interface EditGroupMode {
  groupDetails: OpportunityReviewBoardGroupDetails;
  isEditMode: boolean;
}

export interface OpportunityReviewBoardGroupingPgKpi {
  groupDetails: OpportunityReviewBoardGroupDetails;
  groupKpis: OpportunityReviewTableContent[];
}

export interface GetClassNamesParams {
  baseClasses?: string[];
  isSticky?: boolean;
  sortColumn?: string | null;
  keyHeader?: string | null;
  additionalClasses?: string[];
}
